import { CircleInfoIcon } from "@xola/icons";
import { Alert, Select, Tooltip } from "@xola/ui-kit";
import { SwitchField } from "common";
import { produce } from "immer";
import { keys, set } from "lodash";
import React from "react";
import { Link } from "react-router-dom";
import { CheckoutPreference, CheckoutPriceDisplay, FLOW } from "types";
import { RadioInput } from "../../../Common/RadioInput";
import { getFlowFromWeights, isX2CheckoutSupported } from "@/models/ButtonCode";

interface CheckoutPreferencesFormProps {
    value: CheckoutPreference;
    onChange: (value: CheckoutPreference) => void;
}

export const CheckoutPreferencesForm = ({ value, onChange }: CheckoutPreferencesFormProps) => {
    const checkoutPriceDisplayCopy: { [key: string]: string } = {
        [CheckoutPriceDisplay.HIDE]: "Do not show price",
        [CheckoutPriceDisplay.RANGE]: "Show price range",
        [CheckoutPriceDisplay.LOWEST]: "Show lowest price",
    };

    const handleChange = (key: string, fieldValue: any) => {
        const currentValue = produce(value, (draft) => {
            return set(draft, key, fieldValue);
        });
        onChange(currentValue);
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                {keys(checkoutPriceDisplayCopy).map((priceDisplayType) => {
                    return (
                        <RadioInput
                            key={priceDisplayType}
                            checked={value.priceDisplay === priceDisplayType}
                            onChange={() => handleChange("priceDisplay", priceDisplayType)}
                        >
                            <span onClick={(e) => e.stopPropagation()}>
                                {checkoutPriceDisplayCopy[priceDisplayType]}
                            </span>
                        </RadioInput>
                    );
                })}
            </div>
            <div className="flex flex-col space-y-2">
                <SwitchField
                    size="medium"
                    isChecked={value.includeFees}
                    onChange={(value) => handleChange("includeFees", value)}
                >
                    Include taxes & fees in final price
                </SwitchField>
                <SwitchField
                    size="medium"
                    isChecked={value.showTaxesAndFeesBreakdown}
                    onChange={(value) => handleChange("showTaxesAndFeesBreakdown", value)}
                >
                    <span>
                        Show taxes & fees breakdown
                        <Tooltip content="This will also affect customer facing emails and receipts that contain itemized payment summaries">
                            <CircleInfoIcon className="ml-2" />
                        </Tooltip>
                    </span>
                </SwitchField>
                <SwitchField
                    size="medium"
                    isChecked={value.displayEventDuration}
                    onChange={(value) => handleChange("displayEventDuration", value)}
                >
                    Display duration of event
                </SwitchField>
                <Alert color="primary" className="mt-2 px-6 py-4">
                    CAPTCHA setting has moved to Frictionless Bot Defense under{" "}
                    <Link to="/settings/fraudFighter" className="font-semibold underline">
                        Settings &gt; Fraud Fighter
                    </Link>
                </Alert>
            </div>
            <X2CheckoutFlowPicker
                value={getFlowFromWeights(value)}
                onChange={(value: FLOW) => {
                    handleChange("flowWeights", { [value]: 1 });
                }}
            />
        </div>
    );
};

const X2CheckoutFlowPicker = ({ value, onChange }: { value: FLOW; onChange: (value: FLOW) => void }) => {
    if (!isX2CheckoutSupported()) {
        return null;
    }
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onChange(e.currentTarget.value as FLOW);
    };
    return (
        <div className="w-1/2 space-y-2">
            <span>Select your preferred Date Picker type</span>
            <Select value={value} disabled={false} onChange={handleChange}>
                <option value="mvp">Strip</option>
                <option value="mvp_calendar">Calendar</option>
            </Select>
        </div>
    );
};
